import React, { useState } from 'react';
import { apiCall } from '../utils/api';
import { toast } from './ui/sonner';
import * as XLSX from 'xlsx';
import { 
  Upload, FileText, CheckCircle, XCircle, AlertTriangle, 
  Download, RefreshCw, X, Users, Info
} from 'lucide-react';

export default function BulkWorkerUpload({ onClose, onSuccess }) {
  const [step, setStep] = useState('upload'); // upload, preview, results
  const [csvContent, setCsvContent] = useState('');
  const [sourceFileName, setSourceFileName] = useState('');
  const [preview, setPreview] = useState(null);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState([]);

  const sampleCSV = `email,full_name,phone,skills,certifications,experience_level,notes
john.doe@example.com,John Doe,555-0101,"Nursing,First Aid","RN,CPR",senior,Experienced nurse
jane.smith@example.com,Jane Smith,555-0102,"Healthcare,Patient Care","CNA,BLS",mid,Available weekends
bob.wilson@example.com,Bob Wilson,555-0103,"Medical,Lab Tech","MLT",entry,Recent graduate`;

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setSourceFileName(file.name);
    const lowerName = file.name.toLowerCase();
    const isExcel = lowerName.endsWith('.xlsx') || lowerName.endsWith('.xls');

    if (isExcel) {
      // Parse Excel client-side with SheetJS and convert to CSV for the existing backend parser
      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const data = new Uint8Array(event.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const firstSheetName = workbook.SheetNames[0];
          if (!firstSheetName) {
            toast.error('Excel file has no sheets');
            return;
          }
          const sheet = workbook.Sheets[firstSheetName];
          const csv = XLSX.utils.sheet_to_csv(sheet, { blankrows: false });
          setCsvContent(csv);
          toast.success(`Parsed sheet "${firstSheetName}" — ${csv.split('\n').length - 1} rows`);
        } catch (err) {
          toast.error('Failed to parse Excel file: ' + (err.message || 'Unknown error'));
        }
      };
      reader.onerror = () => toast.error('Failed to read Excel file');
      reader.readAsArrayBuffer(file);
    } else {
      // Treat as CSV / plain text
      const reader = new FileReader();
      reader.onload = (event) => {
        setCsvContent(event.target.result);
      };
      reader.onerror = () => toast.error('Failed to read CSV file');
      reader.readAsText(file);
    }
  };

  const handlePreview = async () => {
    if (!csvContent.trim()) {
      toast.error('Please enter or upload CSV content');
      return;
    }
    
    setLoading(true);
    try {
      const response = await apiCall('/api/agent/workers/parse-csv', {
        method: 'POST',
        body: JSON.stringify({ csv_content: csvContent })
      });
      
      setPreview(response);
      setErrors(response.errors || []);
      setStep('preview');
    } catch (error) {
      toast.error(error.message || 'Failed to parse CSV');
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async () => {
    if (!preview?.preview || preview.preview.length === 0) {
      toast.error('No valid workers to upload');
      return;
    }

    setLoading(true);
    try {
      const response = await apiCall('/api/agent/workers/bulk-upload', {
        method: 'POST',
        body: JSON.stringify({ workers: preview.preview })
      });
      
      setResults(response.results);
      setStep('results');
      
      if (response.summary?.successful > 0) {
        toast.success(`Successfully added ${response.summary.successful} workers!`);
        onSuccess?.();
      }
    } catch (error) {
      toast.error(error.message || 'Failed to upload workers');
    } finally {
      setLoading(false);
    }
  };

  const downloadSample = () => {
    const blob = new Blob([sampleCSV], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'worker_upload_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-cyan-600 p-6 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white/20 backdrop-blur-sm rounded-xl">
                <Upload className="w-8 h-8" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">Bulk Worker Upload</h2>
                <p className="text-white/80">Add multiple workers from CSV or Excel (.xlsx)</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2.5 hover:bg-white/20 rounded-xl transition"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
          
          {/* Progress Steps */}
          <div className="flex items-center gap-4 mt-6">
            {['upload', 'preview', 'results'].map((s, idx) => (
              <div key={s} className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                  step === s ? 'bg-white text-blue-600' :
                  ['upload', 'preview', 'results'].indexOf(step) > idx ? 'bg-white/80 text-blue-600' :
                  'bg-white/20 text-white'
                }`}>
                  {idx + 1}
                </div>
                <span className={`ml-2 text-sm ${step === s ? 'text-white font-medium' : 'text-white/70'}`}>
                  {s.charAt(0).toUpperCase() + s.slice(1)}
                </span>
                {idx < 2 && <div className="w-8 h-0.5 mx-3 bg-white/30" />}
              </div>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {/* Step 1: Upload */}
          {step === 'upload' && (
            <div className="space-y-6">
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-3">
                <Info className="w-5 h-5 text-blue-600 mt-0.5" />
                <div>
                  <p className="font-medium text-blue-900">CSV / Excel Format Requirements</p>
                  <p className="text-sm text-blue-700 mt-1">
                    Required columns: <code className="bg-blue-100 px-1 rounded">email</code>, <code className="bg-blue-100 px-1 rounded">full_name</code>
                  </p>
                  <p className="text-sm text-blue-700">
                    Optional: phone, skills, certifications, experience_level, notes
                  </p>
                  <p className="text-xs text-blue-600 mt-1">
                    Excel files (.xlsx / .xls) are converted to CSV in your browser — only the first sheet is used.
                  </p>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={downloadSample}
                  className="flex items-center gap-2 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 rounded-xl text-gray-700 font-medium transition"
                >
                  <Download className="w-4 h-4" />
                  Download Template
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Upload CSV or Excel File
                  </label>
                  <input
                    type="file"
                    accept=".csv,.xlsx,.xls,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                    onChange={handleFileUpload}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500"
                    data-testid="csv-file-input"
                  />
                  {sourceFileName && (
                    <p className="mt-1 text-xs text-gray-500" data-testid="csv-source-filename">
                      Loaded: <span className="font-mono">{sourceFileName}</span>
                    </p>
                  )}
                </div>

                <div className="text-center text-gray-500 font-medium">OR</div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Paste CSV Content
                  </label>
                  <textarea
                    value={csvContent}
                    onChange={(e) => setCsvContent(e.target.value)}
                    placeholder={sampleCSV}
                    className="w-full h-48 px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                    data-testid="csv-content-input"
                  />
                </div>
              </div>

              <button
                onClick={handlePreview}
                disabled={loading || !csvContent.trim()}
                className="w-full py-3 bg-gradient-to-r from-blue-600 to-cyan-600 text-white rounded-xl font-semibold hover:shadow-lg transition disabled:opacity-50"
                data-testid="preview-csv-btn"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <RefreshCw className="w-5 h-5 animate-spin" />
                    Processing...
                  </span>
                ) : (
                  'Preview Workers'
                )}
              </button>
            </div>
          )}

          {/* Step 2: Preview */}
          {step === 'preview' && preview && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="bg-green-100 p-3 rounded-xl">
                    <Users className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">{preview.total_workers}</p>
                    <p className="text-sm text-gray-500">Workers to add</p>
                  </div>
                </div>
                <button
                  onClick={() => setStep('upload')}
                  className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-xl transition"
                >
                  Back to Edit
                </button>
              </div>

              {errors.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                  <p className="font-medium text-red-900 flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5" />
                    {errors.length} Errors Found
                  </p>
                  <ul className="mt-2 text-sm text-red-700 list-disc list-inside">
                    {errors.slice(0, 5).map((err, idx) => (
                      <li key={idx}>{err}</li>
                    ))}
                    {errors.length > 5 && <li>...and {errors.length - 5} more</li>}
                  </ul>
                </div>
              )}

              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-gray-600">Name</th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-gray-600">Email</th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-gray-600">Skills</th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-gray-600">Level</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {preview.preview?.map((worker, idx) => (
                      <tr key={idx} className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-medium text-gray-900">{worker.full_name}</td>
                        <td className="px-4 py-3 text-gray-600">{worker.email}</td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap gap-1">
                            {worker.skills?.slice(0, 2).map((skill, i) => (
                              <span key={i} className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">
                                {skill}
                              </span>
                            ))}
                            {worker.skills?.length > 2 && (
                              <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full">
                                +{worker.skills.length - 2}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full capitalize">
                            {worker.experience_level}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <button
                onClick={handleUpload}
                disabled={loading || preview.preview?.length === 0}
                className="w-full py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl font-semibold hover:shadow-lg transition disabled:opacity-50"
                data-testid="upload-workers-btn"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <RefreshCw className="w-5 h-5 animate-spin" />
                    Uploading...
                  </span>
                ) : (
                  `Add ${preview.preview?.length || 0} Workers`
                )}
              </button>
            </div>
          )}

          {/* Step 3: Results */}
          {step === 'results' && results && (
            <div className="space-y-6">
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-green-50 p-5 rounded-xl border border-green-200">
                  <div className="flex items-center gap-3 mb-2">
                    <CheckCircle className="w-6 h-6 text-green-600" />
                    <span className="font-semibold text-green-900">Successful</span>
                  </div>
                  <p className="text-3xl font-bold text-green-600">{results.successful?.length || 0}</p>
                </div>
                
                <div className="bg-red-50 p-5 rounded-xl border border-red-200">
                  <div className="flex items-center gap-3 mb-2">
                    <XCircle className="w-6 h-6 text-red-600" />
                    <span className="font-semibold text-red-900">Failed</span>
                  </div>
                  <p className="text-3xl font-bold text-red-600">{results.failed?.length || 0}</p>
                </div>
                
                <div className="bg-yellow-50 p-5 rounded-xl border border-yellow-200">
                  <div className="flex items-center gap-3 mb-2">
                    <AlertTriangle className="w-6 h-6 text-yellow-600" />
                    <span className="font-semibold text-yellow-900">Duplicates</span>
                  </div>
                  <p className="text-3xl font-bold text-yellow-600">{results.duplicates?.length || 0}</p>
                </div>
              </div>

              {results.successful?.length > 0 && (
                <div className="bg-green-50 rounded-xl border border-green-200 p-4">
                  <h4 className="font-semibold text-green-900 mb-3">Successfully Added Workers</h4>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {results.successful.map((w, idx) => (
                      <div key={idx} className="flex items-center justify-between bg-white p-3 rounded-lg">
                        <div>
                          <p className="font-medium text-gray-900">{w.full_name}</p>
                          <p className="text-sm text-gray-500">{w.email}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-gray-500">Temp Password:</p>
                          <code className="text-xs bg-gray-100 px-2 py-1 rounded">{w.temp_password}</code>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {results.failed?.length > 0 && (
                <div className="bg-red-50 rounded-xl border border-red-200 p-4">
                  <h4 className="font-semibold text-red-900 mb-3">Failed to Add</h4>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {results.failed.map((w, idx) => (
                      <div key={idx} className="bg-white p-3 rounded-lg">
                        <p className="font-medium text-gray-900">{w.email}</p>
                        <p className="text-sm text-red-600">{w.reason}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <button
                onClick={onClose}
                className="w-full py-3 bg-gradient-to-r from-blue-600 to-cyan-600 text-white rounded-xl font-semibold hover:shadow-lg transition"
              >
                Done
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
